import type React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { BasicRichText } from "./BasicRichText";
import { useGetMentions } from "./getMentions";
import SimplePreset from "./presets/simple/simple";

const mentionRegex =
  /\(([@#]|\$[a-z]+)\)\[((?:[.a-z,!?<> '_#@0-9\u00C0-\u024F]|-|\(|\))+)\]\(_id:([a-z0-9#]+)\)/gim;

type ProsemirrorJsonNode = {
  content?: ProsemirrorJsonNode[];
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: Array<{ type?: string; attrs?: Record<string, unknown> }>;
};

const constructText = (data: ProsemirrorJsonNode): string => {
  const { content } = data;
  if (content) {
    const separator = data.type === "paragraph" ? "" : "\n";
    return content.map(constructText).join(separator);
  }

  const { text, type, attrs = {}, marks = [] } = data;
  switch (type) {
    case "mentionAtom":
      switch (attrs.name) {
        case "users":
          return `(@)[${attrs.label}](_id:${attrs.id})`;
        case "ideas":
        case "challenges":
        case "groups":
          return `($${attrs.name})[${attrs.label}](_id:${attrs.id})`;
        default:
          return String(attrs.label ?? "");
      }
    case "text":
      if (marks.length) {
        const embeddedMention = marks[0];
        if (
          embeddedMention?.type === "link" &&
          embeddedMention.attrs?.["data-mention-atom-name"] === "users"
        ) {
          return `(@)[${text}](_id:${embeddedMention.attrs?.["data-mention-atom-id"]})`;
        }
      }
      return text ?? "";
    default:
      return text ?? "";
  }
};

const getMentionCaptureGroups = (text: string) => {
  const executedRegex = mentionRegex.exec(text);
  if (!executedRegex || executedRegex.length < 4) return {};
  return { fullName: executedRegex[2], _id: executedRegex[3] };
};

const insertionString = "<?insert!>";

const replaceMentions = (text: string) => {
  if (!text) return "";
  return text.replaceAll(
    mentionRegex,
    `<a target="_blank" class="mention mention-users" rel="noopener noreferrer nofollow" data-mention-atom-id="$3" data-mention-atom-name="users">$2</a>`,
  );
};

const hasExternallyInsertedMention = (text: string) =>
  text.indexOf("(@)") !== -1 && text.endsWith(insertionString);

type MentionTextProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editorRef?: React.RefObject<unknown>;
};

export const MentionText = ({
  value,
  onChange,
  placeholder,
  editorRef,
}: MentionTextProps) => {
  const [mentions, loadMentions] = useGetMentions();
  const [externalValue, setExternalValue] = useState(
    replaceMentions(value) || "",
  );
  const [currentValue, setCurrentValue] = useState(
    replaceMentions(value) || "",
  );
  const [currentHTML, setCurrentHTML] = useState(
    replaceMentions(value) || "<p></p>",
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (value === "") {
      setCurrentValue("");
      setCurrentHTML("<p></p>");
    }

    if (
      !currentValue.endsWith("@") &&
      !externalValue.endsWith("@") &&
      value.endsWith("@")
    ) {
      const replace =
        currentValue.endsWith(" ") || currentValue.length === 0 ?
          "@</p>"
        : " @</p>";
      setCurrentHTML(currentHTML.replace(/<\/p>$/, replace));
    } else if (
      !hasExternallyInsertedMention(currentValue) &&
      !hasExternallyInsertedMention(externalValue) &&
      hasExternallyInsertedMention(value)
    ) {
      const replacedInsertionString = value.replace(insertionString, "");
      const mentionCaptureGroup = getMentionCaptureGroups(value);
      const replacedMention = replacedInsertionString.replace(
        mentionRegex,
        `<a target="_blank" class="mention mention-users" rel="noopener noreferrer nofollow" data-mention-atom-id="${mentionCaptureGroup._id}" data-mention-atom-name="users">${mentionCaptureGroup.fullName}</a>`,
      );
      setCurrentValue(replacedInsertionString);
      onChange(replacedInsertionString);
      setCurrentHTML(
        currentHTML.replace(
          "</p>",
          `${(!currentValue || currentValue.endsWith(" ") ? "" : " ") + replacedMention}</p>`,
        ),
      );
    }
    setExternalValue(value);
  }, [currentValue, externalValue, currentHTML, value, onChange]);

  return (
    <BasicRichText
      placeholder={placeholder || t("mention.guidance")}
      value={currentHTML}
      onChange={(newValue) => {
        setCurrentHTML(newValue);
      }}
      onChangeJson={(newValue) => {
        const constructed = constructText(newValue as ProsemirrorJsonNode);
        setCurrentValue(constructed);
        onChange(constructed);
      }}
      mentions={mentions}
      onSuggest={(kind, query) => loadMentions(kind, query)}
      options={SimplePreset}
      hideMenu
      disableBorder
      editorRef={editorRef}
    />
  );
};
